import { StoreFactories } from "@com.mgmtp.a12.client/client-core/lib/core/store";
import {
	isUaaModifiedOidcUser,
	isUaaOidcUser,
	Role,
	UaaActions,
	UaaOidcModifiedUser,
	UaaSelectors
} from "@com.mgmtp.a12.uaa/uaa-authentication-client";

/**
 * Die Rollen des Benutzers werden von UAA nur einmalig beim Login geladen (`loggedIn` -> `modifyingOidcUser` ->
 * `modifiedOidcUser`). Danach liegen sie ausschliesslich am `UaaOidcModifiedUser` im Store.
 *
 * Wird der Store-Benutzer anschliessend durch einen unmodifizierten OIDC-Benutzer ersetzt, gehen die Rollen verloren.
 * Das passiert vor allem in inaktiven Tabs: Dort drosselt der Browser die Timer des OIDC-Clients, das Access-Token
 * laeuft vor dem Silent Renew ab (`oidc_user_expired` entfernt den Benutzer aus dem Store) und der anschliessend
 * erfolgreiche Renew stellt ueber `oidc_userFound` nur den reinen OIDC-Benutzer ohne Rollen wieder her. Die Anwendung
 * bleibt angemeldet, rollenabhaengige Elemente (z. B. "Antrag erstellen", Loeschen/Duplizieren) verschwinden aber bis
 * zum Neuladen der Seite.
 *
 * Die zuletzt geladenen Rollen werden deshalb hier gepuffert und nach einem Silent Renew ohne erneuten Server-Aufruf
 * wieder an den OIDC-Benutzer geheftet. Vor dem ersten `modifiedOidcUser` ist der Puffer leer, der Login-Ablauf bleibt
 * dadurch unveraendert.
 */
interface CachedRoles {
	readonly subject: string;
	readonly roles: Role[];
	readonly additionalProperties?: Record<string, unknown>;
}

let cache: CachedRoles | undefined;

export const restoreOidcUserRolesMiddleware = StoreFactories.createMiddleware((api, next, action) => {
	if (UaaActions.modifiedOidcUser.match(action)) {
		const { profile, roles, additionalProperties } = action.payload;
		cache = profile?.sub && roles ? { subject: profile.sub, roles, additionalProperties } : undefined;
		return next(action);
	}

	if (
		UaaActions.loggingOut.match(action) ||
		UaaActions.loggedOut.match(action) ||
		UaaActions.oidc_user_signed_out.match(action) ||
		UaaActions.oidc_session_terminated.match(action)
	) {
		cache = undefined;
		return next(action);
	}

	if (!UaaActions.oidc_userFound.match(action) || !cache) {
		return next(action);
	}

	const result = next(action);

	const user = UaaSelectors.user(api.getState());
	// Rollen nur zurueckgeben, wenn es weiterhin derselbe Benutzer ist.
	if (isUaaOidcUser(user) && !isUaaModifiedOidcUser(user) && user.profile?.sub === cache.subject) {
		api.dispatch(
			UaaActions.modifiedOidcUser(new UaaOidcModifiedUser(user, cache.roles, cache.additionalProperties))
		);
	}

	return result;
});
